import React from "react";
import { PoolTokenType } from "impermax-sdk";
import { Link } from "react-router-dom";
import { formatUSD, formatPercentage } from "../../utils/format";
import { useTokenIcon, useLendingPoolUrl } from "../../hooks/useUrlGenerator";
import { RateOption } from ".";
import PairLeftTitle from "../PairLeftTitle";
import { useOffchainBorrowable, useOffchainLendingPool } from "../../hooks/useOffchain";
import useGenericCall from "../../hooks/useGenericCall";

/**
 * Component for a single Lending Pool row.
 */
export default function LendingPoolsRow({ rateOption  } : { rateOption: RateOption }) {
  const data = useGenericCall(useOffchainLendingPool(), async offchainLendingPool => ({
    baseAPR: await offchainLendingPool.getTotalAPR(),
    leverage: await offchainLendingPool.getShownLeverage(),
  }));
  const bData = [PoolTokenType.BorrowableA, PoolTokenType.BorrowableB].map(
    borrowable => useGenericCall(useOffchainBorrowable(borrowable), async offchainBorrowable => {
      const average24hSupplyAPR = await offchainBorrowable.getAverage24hSupplyAPR();
      const average24hBorrowAPR = await offchainBorrowable.getAverage24hBorrowAPR();
      const average7dSupplyAPR = await offchainBorrowable.getAverage7dSupplyAPR();
      const average7dBorrowAPR = await offchainBorrowable.getAverage7dBorrowAPR();
      const supplyAPR = await offchainBorrowable.getSupplyAPR();
      const borrowAPR = await offchainBorrowable.getBorrowAPR();
      const farmingAPR = await offchainBorrowable.getFarmingAPR();
      const selectedBorrowAPR = rateOption === RateOption.weekly ? average7dBorrowAPR : rateOption === RateOption.daily ? average24hBorrowAPR : borrowAPR;
      return {
        symbol: await offchainBorrowable.getSymbol(),
        supplyUSD: await offchainBorrowable.getSupplyUSD(),
        totalBorrowsUSD: await offchainBorrowable.getTotalBorrowsUSD(),
        selectedSupplyAPR: rateOption === RateOption.weekly ? average7dSupplyAPR : rateOption === RateOption.daily ? average24hSupplyAPR : supplyAPR,
        selectedBorrowAPR,
        actualBorrowAPR: selectedBorrowAPR - farmingAPR,
      }
    }, [rateOption])
  );

  const lendingPoolUrl = useLendingPoolUrl();
  const tokenIcons = [useTokenIcon(PoolTokenType.BorrowableA), useTokenIcon(PoolTokenType.BorrowableB)];

  if (!data || !bData[0] || !bData[1]) return (null);

  const leveragedAPR = data.baseAPR * data.leverage - (bData[0].actualBorrowAPR + bData[1].actualBorrowAPR) / 2 * (data.leverage - 1);

  return (
    <Link to={lendingPoolUrl} className="row pairs-table-row">
      <div className="col-7 col-md-5 col-lg-4">
        <div className="currency-name">
          <PairLeftTitle/>
          <div className="currencies-rows-icon d-none d-md-block">
            {bData.map((data, i) => (<div key={i}>
              <img className="currency-icon" src={tokenIcons[i]} />
              {data.symbol.length > 6 ? data.symbol.substring(0, 5) + ".." : data.symbol}
            </div>))}
          </div>
        </div>
      </div>
      <div className="col d-none d-md-block">
        {bData.map((data, i) => (<div key={i}>{formatUSD(data.supplyUSD)}</div>))}
      </div>
      <div className="col d-none d-md-block">
        {bData.map((data, i) => (<div key={i}>{formatUSD(data.totalBorrowsUSD)}</div>))}
      </div>
      <div className="col d-none d-lg-block">
        {bData.map((data, i) => (<div key={i}>{formatPercentage(data.selectedSupplyAPR)}</div>))}
      </div>
      <div className="col d-none d-lg-block">
        {bData.map((data, i) => (<div key={i}>{formatPercentage(data.selectedBorrowAPR)}</div>))}
      </div>
      <div className="col-5 col-md-3 col-lg-2 leveraged-apr-container">
        <div className="base-apr">{ formatPercentage(data.baseAPR) } (1x)</div>
        <div className="leveraged-apr"><span className="percentage">{ formatPercentage(leveragedAPR) }</span> <span className="leverage">({data.leverage}x)</span></div>
      </div>
    </Link>
  );
}
